import { createConfigType } from '@tv2-player/service-container/src/serviceContainer';
import { prerollOverrideUrls } from '../mediaMaking/switchPrerollHost';

// How to add a dev flag:
// 1. Add a new entry to the devFlags object below.
// 2. For Snowpiercer, add the name & default value in each of the files in the deployment folder.
// 2.a Map the environment variable to the dev flag in the registerConfiguration function in the playerServiceConfiguration.tsx file.
//     You also need to add it to the env-type that we use in the file
// 3. For Offspring, add the name & default value in each of the files in the deployment folder.
// 3.a Map the environment variable to the dev flag in the registerConfiguration function in the playerServiceConfiguration.tsx file.
//     You also need to add it to the getPublicRuntimeEnv() function, that we use in that file.
// 4. For Chromecast, map the environment variable to the dev flag in the registerConfiguration function in the playerServiceConfiguration.tsx file. All enviroment variables on Chromecast must start with VITE_

export const devFlags = {
  /**
   * Enables the Overseer plugin. This plugin tracks all plugin calls and sends them to Overseer. This is useful for debugging.
   *
   * Environment variables:
   * Chromecast: VITE_ENABLE_OVERSEER
   * Offspring: ENABLE_OVERSEER
   * Snowpiercer: ENABLE_OVERSEER
   */
  enableOverseer: createBooleanDevFlag('enableOverseer', false),

  /**
   * Enables the VCR plugin. This plugin records the player timeline. You can download it using window.vcr.download(). This is useful for debugging.
   *
   * Environment variables:
   * Chromecast: VITE_ENABLE_PLAYER_VCR
   * Offspring: ENABLE_PLAYER_VCR
   * Snowpiercer: ENABLE_PLAYER_VCR
   */
  enablePlayerVCR: createBooleanDevFlag('enablePlayerVCR', false),

  /**
   * Enables the EPG active tracking for the Nielsen plugin.
   *
   * Environment variables:
   * Chromecast: Enable via the test sender
   * Offspring: USE_ACTIVE_TRACKING_NIELSEN
   * Snowpiercer: Enable via the devflags interface
   */
  useActiveTrackingNielsen: createBooleanDevFlag('useActiveTrackingNielsen', false),
  overseerApiUrl: createConfigType('overseerApiUrl', {
    valueType: 'enum',
    defaultValue: 'https://ws-server.pvp-dev.tv2dev.dk',
    options: ['https://ws-server.pvp-dev.tv2dev.dk', 'http://localhost:3011'],
  }),

  /**
   * Enables test streams for the player. This is useful for testing different streams
   */
  enableTestStreams: createBooleanDevFlag('enableTestStreams', false),
  testStream: createConfigType('testStream', {
    valueType: 'enum',
    defaultValue: 'Looping LAR - Normal',
    options: ['Looping LAR - Normal', 'Looping LAR - Frequency Cap'],
  }),

  enablePrerollOverride: createBooleanDevFlag('enablePrerollOverride', false),
  prerollOverrideUrl: createConfigType('prerollOverrideUrl', {
    valueType: 'enum',
    defaultValue: prerollOverrideUrls.dev,
    options: Object.keys(prerollOverrideUrls).map(
      key => prerollOverrideUrls[key as keyof typeof prerollOverrideUrls]
    ),
  }),
};

/**
 * Creates a dev flag that can be used to enable/disable features in the player.
 */
function createBooleanDevFlag(name: string, defaultValue: boolean) {
  return createConfigType(name, { defaultValue, valueType: 'boolean' });
}

export function getAllBooleanDevFlags() {
  return Object.keys(devFlags)
    .map(key => key as keyof typeof devFlags)
    .filter(key => devFlags[key].valueType === 'boolean');
}
